// Fixing typos in a published rubric that has already been observed against.
//
// Deliberately NOT the draft editor with buttons hidden: that editor's save is
// a whole-tree replacement, and everything it can express — adding, deleting,
// re-ordering, re-gating — is exactly what a version somebody has been scored
// against must not lose. This surface can only send text, one row and one
// language at a time, and each field carries the value it expects to replace so
// a colleague's save cannot be silently overwritten. The one structural change
// it also takes is which subjects a standard is scored on: nothing submitted is
// rewritten — analytics read past lessons through the current scope.
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { inputBase, LanguageTabs, TranslationReference } from "./LocalizedFields";
import SubjectScopeField, { SubjectNowRequired } from "./SubjectScopeField";
import {
  apiTreeToEditorState,
  findScopeProblem,
  hasText,
  isScopeChanged,
  scopePayload,
  textFromApi,
  trimText,
} from "./templateTree";
import { guardUnsavedWork } from "../../utils/helpers/unsavedWork";
import { LANGUAGE_IDS } from "../../lang/languages";
import { correctWording, setSubjectScope } from "../../services/rubricService";
import { formatApiError } from "../../utils/helpers/apiErrors";

const STANDARD_FIELDS = ["title", "description"];
const QUESTION_FIELDS = ["text", "short_label", "pd_focus", "help_text"];

const rowKey = (row, uuid) => `${row}:${uuid}`;

/** {"question:uuid": {text: {en,kk,ru}, …}} for every correctable field.
 *  Fields that are empty in every language are skipped: the rubric carries
 *  optional ones (pd_focus, help_text) that most templates never use, and an
 *  empty box invites authoring where only correcting is allowed. */
const snapshotOf = (version) => {
  const snapshot = {};
  (version?.standards || []).forEach((std) => {
    const fields = {};
    STANDARD_FIELDS.forEach((field) => {
      const value = textFromApi(std, field);
      if (field === "title" || hasText(value)) fields[field] = value;
    });
    snapshot[rowKey("standard", std.uuid)] = fields;
    (std.questions || []).forEach((q) => {
      const qFields = {};
      QUESTION_FIELDS.forEach((field) => {
        const value = textFromApi(q, field);
        if (field === "text" || hasText(value)) qFields[field] = value;
      });
      snapshot[rowKey("question", q.uuid)] = qFields;
    });
  });
  return snapshot;
};

/** Every language that actually changed, as one correction per field.
 *  Walks the OVERRIDES, not the whole tree: on a 70-question rubric the tree
 *  is ~170 fields and this runs on every keystroke. */
const buildCorrections = (original, overrides) => {
  const corrections = [];
  Object.entries(overrides).forEach(([key, fields]) => {
    const [row, uuid] = key.split(":");
    Object.entries(fields).forEach(([field, value]) => {
      const before = original[key]?.[field] || {};
      const after = trimText(value);
      const changed = LANGUAGE_IDS.filter(
        (id) => (after[id] || "") !== (before[id] || "").trim(),
      );
      if (!changed.length) return;
      corrections.push({
        row,
        uuid,
        field,
        expected: Object.fromEntries(changed.map((id) => [id, before[id] || ""])),
        value: Object.fromEntries(changed.map((id) => [id, after[id] || ""])),
      });
    });
  });
  return corrections;
};

// Memoized, and given the address of its own cell rather than a closure over
// it: without both, one keystroke re-renders every input in the rubric.
const Field = memo(function Field({
  rowKey,
  field,
  label,
  value,
  lang,
  showReference,
  onChange,
  multiline = false,
}) {
  const Control = multiline ? "textarea" : "input";
  return (
    <div>
      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">
        {label}
      </label>
      <Control
        {...(multiline ? { rows: 2 } : { type: "text" })}
        value={value?.[lang] || ""}
        onChange={(e) => onChange(rowKey, field, { ...value, [lang]: e.target.value })}
        aria-label={label}
        className={`w-full px-3 py-2 text-sm ${inputBase}`}
      />
      {showReference && (
        <TranslationReference
          value={value}
          lang={lang}
          onCopy={(from) => onChange(rowKey, field, { ...value, [lang]: value[from] })}
        />
      )}
    </div>
  );
});

export default function WordingEditor({ version, subjectCatalog, lang, onLangChange, onSaved }) {
  const { t } = useTranslation();
  const original = useMemo(() => snapshotOf(version), [version]);
  // The scope edits ride on the same tree shape the draft editor validates, so
  // both surfaces refuse the same gate the server would.
  const tree = useMemo(() => apiTreeToEditorState(version), [version]);
  const [scopeOverrides, setScopeOverrides] = useState({});
  // ONLY the fields the author touched. A full copy of the tree would be a
  // second source of truth for the same text, which is why this component used
  // to need remounting to accept a reloaded version — with overrides, a new
  // `version` flows in on its own and edits elsewhere survive a conflict.
  const [overrides, setOverrides] = useState({});
  const [showReference, setShowReference] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const corrections = useMemo(
    () => buildCorrections(original, overrides),
    [original, overrides],
  );

  const setField = useCallback(
    (key, field, value) =>
      setOverrides((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } })),
    [],
  );

  const valueOf = (key, field) => overrides[key]?.[field] ?? original[key]?.[field];

  // The tree with the author's scope edits applied: what the rows show and
  // what gets validated.
  const scoped = useMemo(
    () => tree.map((std) => ({ ...std, ...scopeOverrides[std.uuid] })),
    [tree, scopeOverrides],
  );
  // The standards whose scope actually changed, in the PATCH's shape.
  const scopeChanges = useMemo(
    () =>
      scoped
        .filter((std, i) => isScopeChanged(std, tree[i]))
        .map((std) => ({ uuid: std.uuid, ...scopePayload(std) })),
    [scoped, tree],
  );
  const pending = corrections.length + scopeChanges.length;

  // Same protection the draft editor gives unsaved work — and the same shared
  // guard, so a sidebar link or a language switch asks too.
  useEffect(() => guardUnsavedWork(pending > 0), [pending]);

  const save = async () => {
    // A blank correction would erase that language column rather than fix it,
    // and the server refuses it — say so here instead of showing a 400.
    const blank = corrections.find((c) =>
      Object.values(c.value).some((text) => !text.trim()),
    );
    if (blank) {
      toast.error(
        `${t("templates.wordingBlank")} (${t(`templates.field_${blank.field}`)})`,
      );
      return;
    }
    const scopeProblem = scopeChanges.length && findScopeProblem(scoped);
    if (scopeProblem) {
      toast.error(t(scopeProblem));
      return;
    }
    setIsSaving(true);
    // Scope first: it has no conflict semantics, so a wording 409 after it
    // still leaves the scope saved and only the text to redo. Its result goes
    // up at once — the server has it, whatever the wording request does next.
    let scopeSaved = false;
    try {
      if (scopeChanges.length) {
        onSaved(
          await setSubjectScope({ uuid: version.uuid, body: { standards: scopeChanges } }),
        );
        setScopeOverrides({});
        scopeSaved = true;
      }
      if (corrections.length) {
        const updated = await correctWording({ uuid: version.uuid, body: { corrections } });
        toast.success(
          t("templates.wordingSaved", { count: updated.applied_corrections?.length ?? 0 }),
        );
        setOverrides({});
        onSaved(updated);
      } else {
        toast.success(t("subjectScope.saved"));
      }
    } catch (err) {
      // A 409: somebody saved first. Reload the stored text — the overrides
      // stay, so the author keeps their other edits and `expected` is
      // recomputed against what is actually there now.
      const conflict = err?.response?.status === 409;
      const reason = conflict
        ? t("templates.wordingConflict")
        : formatApiError(err, t("templates.wordingSaveFailed"));
      toast.error(scopeSaved ? t("templates.scopeSavedWordingFailed", { reason }) : reason);
      if (conflict) onSaved(null);
    } finally {
      setIsSaving(false);
    }
  };

  const fieldLabel = (field) => t(`templates.field_${field}`);

  return (
    <div className="space-y-5">
      <div className="px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-xs font-semibold text-amber-800 leading-relaxed">
        {t("templates.wordingBanner")}
      </div>

      <div className="sticky top-0 z-30 -mx-2 px-2 pt-1 pb-3 bg-slate-50/95 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <LanguageTabs value={lang} onChange={onLangChange} />
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showReference}
                onChange={(e) => setShowReference(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-2 focus:ring-indigo-500/20"
              />
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {t("templates.showOtherLanguages")}
              </span>
            </label>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-bold text-slate-500 tabular-nums">
              {t("templates.wordingPending", { count: pending })}
            </span>
            <button
              type="button"
              onClick={save}
              disabled={isSaving || pending === 0}
              className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold whitespace-nowrap hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? t("common.saving") : t("templates.wordingSave")}
            </button>
          </div>
        </div>
        <SubjectNowRequired stored={version.standards} standards={scoped} />
      </div>

      {(version.standards || []).map((std, stdIdx) => {
        const key = rowKey("standard", std.uuid);
        const scope = scoped[stdIdx];
        return (
          <div
            key={std.uuid}
            className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden"
          >
            <div className="px-4 py-3 bg-slate-50/50 flex items-start gap-3">
              <span className="w-7 h-7 rounded-lg bg-indigo-600 text-white text-xs font-black flex items-center justify-center shrink-0">
                {stdIdx + 1}
              </span>
              <div className="flex-1 min-w-0 space-y-3">
                {Object.keys(original[key] || {}).map((field) => (
                  <Field
                    key={field}
                    rowKey={key}
                    field={field}
                    label={fieldLabel(field)}
                    value={valueOf(key, field)}
                    lang={lang}
                    showReference={showReference}
                    multiline={field === "description"}
                    onChange={setField}
                  />
                ))}
                <SubjectScopeField
                  subjectScope={scope.subjectScope}
                  subjects={scope.subjects}
                  catalog={subjectCatalog}
                  stored={tree[stdIdx]}
                  onChange={(next) =>
                    setScopeOverrides((prev) => ({ ...prev, [std.uuid]: next }))
                  }
                />
              </div>
            </div>

            <div className="p-4 space-y-4">
              {(std.questions || []).map((q, qIdx) => {
                const qKey = rowKey("question", q.uuid);
                return (
                  <div key={q.uuid} className="flex items-start gap-3">
                    <span className="mt-2 text-[11px] font-black text-slate-400 tabular-nums shrink-0 w-8">
                      {stdIdx + 1}.{qIdx + 1}
                    </span>
                    <div className="flex-1 min-w-0 space-y-3">
                      {Object.keys(original[qKey] || {}).map((field) => (
                        <Field
                          key={field}
                          rowKey={qKey}
                          field={field}
                          label={fieldLabel(field)}
                          value={valueOf(qKey, field)}
                          lang={lang}
                          showReference={showReference}
                          multiline={field !== "short_label"}
                          onChange={setField}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
